/**
 * Thin wrapper around @tanstack/react-query for this app's API calls.
 *
 * Gives every caller the same contract: caching by key, AbortController
 * cancellation (react-query aborts the in-flight fetch when the last
 * observer unmounts), a real error surface (no `.catch(() => [])`), optional
 * polling that pauses while the tab is hidden (refetchIntervalInBackground
 * is off) and backs off on consecutive failures.
 */
import {
  useQuery,
  type QueryKey,
  type UseQueryResult,
} from "@tanstack/react-query";

export type ApiQueryOptions = {
  /** Poll every `pollMs` while mounted and visible. */
  pollMs?: number;
  enabled?: boolean;
  /** Cache freshness window; default 15s. */
  staleTimeMs?: number;
};

/** Grow the interval on consecutive failed polls: ×1, ×2, ×4 (cap). */
export function backoffMs(baseMs: number, failureCount: number): number {
  const factor = Math.min(2 ** Math.min(failureCount, 2), 4);
  return baseMs * factor;
}

export function useApiQuery<T>(
  key: QueryKey,
  fn: (signal: AbortSignal) => Promise<T>,
  options: ApiQueryOptions = {},
): UseQueryResult<T> {
  const { pollMs, enabled = true, staleTimeMs = 15_000 } = options;
  return useQuery<T>({
    queryKey: key,
    queryFn: ({ signal }) => fn(signal),
    enabled,
    staleTime: staleTimeMs,
    retry: pollMs ? false : 1,
    refetchInterval: pollMs
      ? (query) => backoffMs(pollMs, query.state.fetchFailureCount)
      : undefined,
    refetchIntervalInBackground: false,
  });
}
